import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Empty,
  Flex,
  Popconfirm,
  Select,
  Table,
  TableColumnsType,
  Upload
} from 'antd'
import { SelectProps } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceDeleteContractEndpoint,
  useCounterpartServiceGetContractListEndpoint,
  useCounterpartServiceGetContractListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ContractSortBy,
  GetContractListResponseItem
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { CustomItemRender, message, SearchBar, Text } from 'src/components'
import {
  FileDocument,
  onDownloadFile,
  useDefaultSortingItem,
  useTablePagination
} from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import {
  formErrorHandler,
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

import { useStyles } from './styles'

type Props = {
  isCounterpart?: boolean
}

export const ContractList = ({ isCounterpart }: Props) => {
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('')

  const { t } = useLocales()
  const pagination = useTablePagination()
  const navigate = useNavigate()
  const { styles } = useStyles()

  const queryClient = useQueryClient()

  const { id } = useParams()
  const theme = useTheme()

  const { defaultSortingOption } = useDefaultSortingItem()

  const orderingOptions: SelectProps['options'] = [
    defaultSortingOption,
    {
      value: ContractSortBy.CONTRACT_NUMBER,
      label: t('counterparts.sort_contract_by_number')
    },
    {
      value: `-${ContractSortBy.CONTRACT_NUMBER}`,
      label: t('counterparts.sort_contract_by_number_desc')
    },
    {
      value: ContractSortBy.START_DATE,
      label: t('counterparts.sort_contract_by_date')
    },
    {
      value: `-${ContractSortBy.START_DATE}`,
      label: t('counterparts.sort_contract_by_date_desc')
    },
    {
      value: `${ContractSortBy.END_DATE}`,
      label: t('counterparts.sort_contract_by_end_date')
    },
    {
      value: `-${ContractSortBy.END_DATE}`,
      label: t('counterparts.sort_contract_by_end_date_desc')
    }
  ]

  const queryParams = {
    pageSize: DEFAULT_PER_PAGE,
    pageNumber: Number(pagination.current),
    counterpartId: String(id),
    ...(search && {
      searchTerm: search
    }),
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering) as ContractSortBy,
      sortDirection: getOrderingDirection(ordering)
    })
  }

  const onSearch = (value: string) => {
    setSearch(value)
    resetPagination(pagination)
  }

  const { data, isLoading } =
    useCounterpartServiceGetContractListEndpoint(queryParams)

  const { mutate } = useCounterpartServiceDeleteContractEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useCounterpartServiceGetContractListEndpointKey]
      })
      message.success(t('counterparts.contact_deleted'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const columns = useMemo(
    (): TableColumnsType<GetContractListResponseItem> => [
      {
        key: 'contractNumber',
        title: t('counterparts.contract_number'),
        render: ({ signedOn, contractNumber }) => (
          <Flex vertical>
            <Text weight={400}>#{contractNumber}</Text>
            <Text weight={400} color={theme.colorTextTertiary}>
              {t('counterparts.signed_date')}:{' '}
              {dayjs(signedOn).format(dayMonthYearBackSlash)}
            </Text>
          </Flex>
        ),
        width: '15%'
      },
      {
        key: 'startDate',
        width: '10%',
        title: t('counterparts.active_from'),
        render: ({ startDate }) =>
          dayjs(startDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'endDate',
        width: '10%',
        title: t('counterparts.active_to'),
        render: ({ endDate }) => dayjs(endDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'files',
        width: 300,
        title: t('counterparts.additional_files'),
        render: (_, { files }) => (
          <Upload
            customRequest={() => null}
            itemRender={(_, file: FileDocument) => (
              <CustomItemRender
                file={file}
                onDownload={() => onDownloadFile(t, file?.id, file?.name)}
              />
            )}
            fileList={
              files?.map((file, index) => ({
                ...file,
                uid: String(index),
                name: file?.name || `file-${index}`
              })) || []
            }
            disabled
          />
        )
      },

      {
        key: 'description',
        title: t('counterparts.description'),
        render: ({ description }) => description ?? '-'
      },
      {
        width: 100,
        key: 'actions',
        title: t('common.action'),
        align: 'center',
        render: ({ id: contractId }) => (
          <Flex gap={8}>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                navigate(
                  isCounterpart
                    ? paths.counterpart.contractEdit(id, contractId)
                    : paths.institutions.contractEdit(id, contractId)
                )
              }
            />

            <Popconfirm
              placement="leftBottom"
              description={t('common.delete_description')}
              title={t('counterparts.delete_contract')}
              onConfirm={() => mutate({ id: contractId })}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Flex>
        )
      }
    ],
    [t]
  )

  const onAdd = () => {
    navigate(
      isCounterpart
        ? paths.counterpart.contratAdd(id)
        : paths.institutions.contratAdd(id)
    )
  }

  return (
    <Card>
      <Flex vertical gap={24}>
        <Text size="xxl">{t('counterparts.contracts')}</Text>

        {(data?.totalCount ?? 0) > 0 && (
          <Flex justify="end" gap={16}>
            <SearchBar
              enterButton={false}
              className="full-width"
              placeholder={t('common.search')}
              onSearch={(val) => onSearch(val)}
              maxWidth={260}
            />
            <Select
              options={orderingOptions}
              onSelect={(opt) => setOrdering(opt)}
              placeholder={t('common.sort_by')}
              className={styles.sortBySelect}
              popupMatchSelectWidth
            />
            <Button icon={<PlusOutlined />} type="primary" onClick={onAdd}>
              {t('counterparts.add_new_contract')}
            </Button>
          </Flex>
        )}
        <Table
          loading={isLoading}
          rowKey="id"
          bordered
          dataSource={data?.data}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
          locale={{
            emptyText: (
              <Empty description={t('counterparts.no_contracts_found')}>
                <Button type="primary" onClick={onAdd}>
                  {t('counterparts.add_new_contract')}
                </Button>
              </Empty>
            )
          }}
        />
      </Flex>
    </Card>
  )
}
